import React from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  VStack,
} from '@chakra-ui/react';

interface LeadFormProps {
  data: Record<string, any>;
  options: Record<string, any[]>;
  onChange: (data: Record<string, any>) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export default function LeadForm({
  data,
  options,
  onChange,
  onSubmit,
  isSubmitting,
}: LeadFormProps) {
  const set = (key: string, value: any) =>
    onChange({ ...data, [key]: value });

  return (
    <VStack align="stretch" spacing={3}>
      <SimpleGrid columns={2} spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs">Business Name</FormLabel>
          <Input
            size="sm"
            value={data.business_name ?? ''}
            onChange={(e) => set('business_name', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">DBA Name</FormLabel>
          <Input
            size="sm"
            value={data.dba_name ?? ''}
            onChange={(e) => set('dba_name', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">Contact Name</FormLabel>
          <Input
            size="sm"
            value={data.contact_name ?? ''}
            onChange={(e) => set('contact_name', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">Contact Title</FormLabel>
          <Input
            size="sm"
            value={data.contact_title ?? ''}
            onChange={(e) => set('contact_title', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">Phone</FormLabel>
          <Input
            size="sm"
            value={data.phone ?? ''}
            onChange={(e) => set('phone', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">Email</FormLabel>
          <Input
            size="sm"
            value={data.email ?? ''}
            onChange={(e) => set('email', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">Address</FormLabel>
          <Input
            size="sm"
            value={data.address ?? ''}
            onChange={(e) => set('address', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">City</FormLabel>
          <Input
            size="sm"
            value={data.city ?? ''}
            onChange={(e) => set('city', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">State</FormLabel>
          <Input
            size="sm"
            value={data.state ?? ''}
            onChange={(e) => set('state', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">Zip</FormLabel>
          <Input
            size="sm"
            value={data.zip ?? ''}
            onChange={(e) => set('zip', e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">Employees</FormLabel>
          <Input
            size="sm"
            type="number"
            value={data.employees ?? ''}
            onChange={(e) => set('employees', Number(e.target.value))}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">Weekly Payroll</FormLabel>
          <Input
            size="sm"
            type="number"
            value={data.weekly_payroll ?? ''}
            onChange={(e) => set('weekly_payroll', Number(e.target.value))}
          />
        </FormControl>

        <SelectField
          label="Status"
          value={data.status_id}
          items={options.statuses}
          valueKey="id"
          labelKey="name"
          onChange={(v) => set('status_id', Number(v))}
        />

        <SelectField
          label="Source"
          value={data.source_id}
          items={options.sources}
          valueKey="id"
          labelKey="name"
          onChange={(v) => set('source_id', Number(v))}
        />

        <SelectField
          label="Temperature"
          value={data.temperature_id}
          items={options.temperatures}
          valueKey="id"
          labelKey="name"
          onChange={(v) => set('temperature_id', Number(v))}
        />

        <SelectField
          label="Industry"
          value={data.industry_id}
          items={options.industries}
          valueKey="id"
          labelKey="name"
          onChange={(v) => set('industry_id', Number(v))}
        />

        <SelectField
          label="Language"
          value={data.language}
          items={options.languages}
          onChange={(v) => set('language', v)}
        />

        <SelectField
          label="Carrier"
          value={data.carrier}
          items={options.carriers}
          onChange={(v) => set('carrier', v)}
        />

        <SelectField
          label="Company Type"
          value={data.company_type}
          items={options.company_types}
          onChange={(v) => set('company_type', v)}
        />

        <SelectField
          label="Program Type"
          value={data.program_type}
          items={options.program_types}
          onChange={(v) => set('program_type', v)}
        />

        <SelectField
          label="Service Tier"
          value={data.customer_service_tier}
          items={options.customer_service_tiers}
          onChange={(v) => set('customer_service_tier', v)}
        />

        {options.brokers && (
          <SelectField
            label="Broker"
            value={data.broker_id}
            items={options.brokers}
            valueKey="id"
            labelKey="name"
            onChange={(v) => set('broker_id', Number(v))}
          />
        )}
      </SimpleGrid>

      <Button
        colorScheme="purple"
        size="sm"
        onClick={onSubmit}
        isLoading={isSubmitting}
        alignSelf="flex-start"
      >
        Create Lead
      </Button>
    </VStack>
  );
}

function SelectField({
  label,
  value,
  items,
  valueKey = 'value',
  labelKey = 'label',
  onChange,
}: {
  label: string;
  value: any;
  items: any[];
  valueKey?: string;
  labelKey?: string;
  onChange: (value: string) => void;
}) {
  return (
    <FormControl>
      <FormLabel fontSize="xs">{label}</FormLabel>
      <Select
        size="sm"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">—</option>
        {items.map((item) => (
          <option key={item[valueKey]} value={item[valueKey]}>
            {item[labelKey]}
          </option>
        ))}
      </Select>
    </FormControl>
  );
}
